import { motion } from "framer-motion";
import { Award, ArrowUpRight } from "lucide-react";

const education = [
  {
    id: "05",
    school: "Golden Gate University",
    college: "Ageno School of Business",
    degree: "Business Administration and Management",
    grade: "3.9/4.0 GPA",
    details: "Specialised in Marketing and Project Management.",
    color: "bg-purple-600",
    text: "text-purple-600"
  },
  {
    id: "01",
    school: "University of California, Berkeley",
    college: "Haas School of Business",
    degree: "Business Administration & Management",
    grade: "3.7 GPA",
    details: "Specialized in Technology Entrepreneurship, Fintech, and Pricing Strategies. Focused on Decision Making frameworks and International Marketing dynamics.",
    color: "bg-blue-600",
    text: "text-blue-600"
  },
  {
    id: "02",
    school: "SRM IST Chennai",
    college: "Main Campus, Chennai",
    degree: "B.Tech in Computer Science",
    grade: "9.4/10",
    details: "Leveraged flexible curriculum to explore cross-disciplinary ventures. Built a strong technical foundation while launching multiple student-led initiatives.",
    color: "bg-amber-500",
    text: "text-amber-500"
  },
  {
    id: "03",
    school: "University of California, Berkeley",
    college: "College of Engineering",
    degree: "Tech Entrepreneurship & Innovation",
    grade: "3.6/4",
    details: "Immersive program bridging engineering and product viability. Hands-on experience with prototyping and agile methodologies.",
    color: "bg-blue-800",
    text: "text-blue-800"
  },
  {
    id: "04",
    school: "Asia University (TW)",
    college: "Taiwan Exchange",
    degree: "Investments & Securities",
    grade: "9.0/10",
    details: "Intensive exchange semester focusing on Asian financial markets, investment vehicles, and securities analysis.",
    color: "bg-red-600",
    text: "text-red-600"
  }
];

const EducationSection = () => {
  return (
    <section id="education" className="bg-slate-50 py-32 overflow-hidden perspective-1000">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        
        {/* --- HEADER (Centered for contrast) --- */}
        <div className="text-center max-w-3xl mx-auto mb-24">
           <motion.div 
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: false }}
             className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm mb-6"
           >
              <Award className="w-4 h-4 text-slate-900" />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-900">Academic Background</span>
           </motion.div>

           <h2 className="text-5xl md:text-7xl font-bold text-slate-900 tracking-tighter mb-6">
             The Foundation.
           </h2>
           <p className="text-slate-500 text-lg">
             A timeline of formal training that shaped my strategic and technical depth.
           </p>
        </div>

        {/* --- THE 3D MASONRY GRID --- */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
           
           {/* Column 1 (Normal) */}
           <div className="flex flex-col gap-8 lg:gap-12">
              {education.filter((_, i) => i % 2 === 0).map((edu, i) => (
                 <EducationCard key={edu.id} edu={edu} index={i} />
              ))}
           </div>

           {/* Column 2 (Pushed Down for Stagger Effect) */}
           <div className="flex flex-col gap-8 lg:gap-12 md:mt-24">
              {education.filter((_, i) => i % 2 !== 0).map((edu, i) => (
                 <EducationCard key={edu.id} edu={edu} index={i} />
              ))}
           </div>

        </div>

      </div>
    </section>
  );
};

const EducationCard = ({ edu, index }: any) => {
  return (
    <motion.div
       initial={{ opacity: 0, y: 100, rotateX: -15 }} // 3D STARTING POSITION
       whileInView={{ opacity: 1, y: 0, rotateX: 0 }} // FLIP UP
       viewport={{ once: false, margin: "-15%" }}
       transition={{ 
         duration: 0.8, 
         type: "spring", 
         bounce: 0.2,
         delay: index * 0.1 
       }}
       className="group relative bg-white p-10 rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500"
    >
        {/* Top Decoration Line */}
        <div className={`absolute top-0 left-1/2 -translate-x-1/2 h-1 w-20 rounded-b-full ${edu.color}`} />

        <div className="flex justify-end items-start mb-6 mt-2">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-slate-50 group-hover:bg-slate-900 transition-colors duration-500`}>
            <ArrowUpRight className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
          </div>
        </div>

        <div className="mb-8">
            <h3 className="text-3xl font-bold text-slate-900 mb-2 leading-tight group-hover:text-blue-600 transition-colors">
                {edu.school}
            </h3>
            <div className={`text-sm font-bold uppercase tracking-widest mb-1 ${edu.text}`}>
                {edu.college}
            </div>
            <div className="text-lg text-slate-600 font-medium">
                {edu.degree}
            </div>
        </div>

        <div className="space-y-6">
            <p className="text-slate-500 leading-relaxed text-sm border-t border-slate-100 pt-6">
                {edu.details}
            </p>
            
            <div className="flex items-center gap-3">
                <div className={`px-4 py-2 rounded-lg bg-slate-50 border border-slate-100`}>
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mr-2">Grade</span>
                    <span className="text-lg font-bold text-slate-900">{edu.grade}</span>
                </div>
            </div>
        </div>

    </motion.div>
  );
};

export default EducationSection;